<template>
  <modal
    :id="modalId"
    :title="$t('create_testimonial_title')"
    @close="closeModal"
    is-simplified-modal
    :show-footer="true"
  >
    <template #body>
      <div class="create-testimonial-modal p-6">
        <!-- Collaboration message -->
        <div class="collaboration-message">
          <p class="message-title">{{ $t('collaboration_drives_progress') }}</p>
          <p class="message-text">{{ $t('testimonial_guidance_message') }}</p>
        </div>

        <!-- Target actor: read-only when opened from actor detail -->
        <form-group
          v-if="targetActor"
          :label="$t('create_testimonial_target_label')"
        >
          <div class="actor-search__selected actor-search__selected--readonly">
            <span>{{ targetActor.name }}</span>
          </div>
        </form-group>

        <!-- Target actor search (when opened without a pre-selected actor) -->
        <form-group
          v-if="!targetActor"
          :label="$t('create_testimonial_target_label')"
          :required="true"
        >
          <div class="actor-search" ref="actorSearchRef">
            <DsInput
              v-model="actorSearchQuery"
              :placeholder="$t('create_testimonial_target_placeholder')"
              is-simplified
              @input="onActorSearchInput"
              @focus="onActorSearchFocus"
              @blur="onActorSearchBlur"
            />
            <div v-if="selectedTargetActor" class="actor-search__selected">
              <span>{{ selectedTargetActor.label }}</span>
              <button type="button" @click="clearTargetActor">×</button>
            </div>
            <Teleport to="body">
              <ul
                v-if="actorSearchOpen && (actorSearchResults.length || actorSearchLoading)"
                class="actor-search__dropdown"
                :style="actorSearchDropdownStyle"
                @mousedown.prevent
              >
                <li v-if="actorSearchLoading" class="actor-search__dropdown-loading">
                  <icon name="spinner" class="animate-spin"/> {{ $t('loading') }}...
                </li>
                <li
                  v-for="result in actorSearchResults"
                  :key="result.value"
                  class="actor-search__dropdown-item"
                  @mousedown.prevent="selectTargetActor(result)"
                >
                  {{ result.label }}
                </li>
              </ul>
            </Teleport>
          </div>
        </form-group>

        <div v-if="!eligibleActors.length && !loadingEligible && resolvedTargetActor" class="no-eligible-actors">
          <icon name="info-circle" class="text-orange-500 tw-flex-shrink-0"/>
          <div>
            <h4>{{ $t('no_claimed_companies_title') }}</h4>
            <p>{{ $t('no_claimed_companies_description') }}</p>
          </div>
        </div>

        <div v-if="loadingEligible" class="loading-state">
          <icon name="spinner" class="animate-spin"/>
          <span>{{ $t('loading') }}...</span>
        </div>

        <form v-if="eligibleActors.length && !loadingEligible && resolvedTargetActor" @submit.prevent="submitTestimonial">
          <form-group
            :label="$t('create_testimonial_author_label')"
            :errors="errors.author_actor_id"
            :required="true"
          >
            <Dropdown
              v-model="form.author_actor_id"
              @update:model-value="onAuthorChange"
              :options="eligibleActors"
              is-simplified
            />
            <!--            <select
                          v-model="form.author_actor_id"
                          required
                          @change="onAuthorChange"
                        >
                          <option value="" v-if="eligibleActors.length > 1">{{ $t('create_testimonial_select_company') }}</option>
                          <option
                            v-for="actor in eligibleActors"
                            :key="actor.id"
                            :value="actor.id"
                          >
                            {{ actor.name }}
                            <span v-if="actor.role"> ({{ actor.role }})</span>
                          </option>
                        </select>-->
            <p class="form-help-text" v-if="!selectedAuthor?.relationship">
              {{ $t('create_testimonial_author_help') }}
            </p>
          </form-group>

          <!-- Relationship detected banner -->
          <div v-if="selectedAuthor?.relationship" class="relationship-detected-banner">
            <icon name="link"/>
            <span>{{ $t('relationship_detected') }}: <strong>{{ selectedAuthor.relationship.alias || selectedAuthor.relationship.label }}</strong></span>
          </div>

          <!-- Only show role and relationship fields if NO relationship detected -->
          <form-group
            v-if="form.author_actor_id && !selectedAuthor?.relationship"
            :label="$t('your_role')"
            :errors="errors.author_role"
          >
            <DsInput
              v-model="form.author_role"
              type="text"
              class="form-control"
              is-simplified
              :placeholder="$t('enter_your_role')"
            />
            <p class="form-help-text">
              {{ $t('role_help_text') }}
            </p>
          </form-group>

          <form-group
            v-if="form.author_actor_id && !selectedAuthor?.relationship"
            :label="$t('relationship_type')"
            :errors="errors.relationship_type"
          >
            <Dropdown
              v-model="form.relationship_type"
              :options="relationshipTypes"
              is-simplified
            />
            <!--            <select
                          v-model="form.relationship_type"
                          class="form-control"
                        >
                          <option value="">{{ $t('select_relationship_type') }}</option>
                          <option
                            v-for="(label, value) in relationshipTypes"
                            :key="value"
                            :value="value"
                          >
                            {{ label }}
                          </option>
                        </select>-->
          </form-group>

          <form-group
            :label="$t('create_testimonial_content_label')"
            :required="true"
          >
            <ds-textarea
              class="!h-10 mb-4"
              v-model="form.content"
              :placeholder="$t('create_testimonial_content_placeholder')"
              :default-text="$t('create_testimonial_content_placeholder')"
              :rows="5"
              required
              is-simplified
              autoresize
              :class="{ 'invalid': !isContentValid && form.content.length > 0 }"
            />
            <div class="!mt-4 textarea-footer">
              <p class="form-help-text" :class="{ 'text-red-500': !isContentValid && form.content.length > 0 }">
                {{ contentValidationMessage || $t('create_testimonial_content_help') }}
              </p>
              <div class="character-count" :class="{
              'text-red-500': form.content.length > 2000 || (form.content.length > 0 && form.content.length < 10),
              'text-green-500': form.content.length >= 10 && form.content.length <= 2000
            }">
                {{ form.content.length }}/2000
              </div>
            </div>
          </form-group>

          <form-group
            :label="$t('create_testimonial_rating_label')"
            :errors="errors.rating"
          >
            <div class="rating-input">
              <button
                v-for="star in 5"
                :key="star"
                type="button"
                class="star-button"
                :class="{ active: form.rating >= star }"
                @click="setRating(star)"
              >
                <icon name="star-outline"/>
              </button>
              <DsButton
                v-if="form.rating"
                type="button"
                class="clear-rating"
                @click="setRating(null)"
                :label="$t('create_testimonial_clear_rating')"
              >
              </DsButton>
            </div>
            <p class="form-help-text">
              {{ $t('create_testimonial_rating_help') }}
            </p>
          </form-group>
        </form>
      </div>
    </template>
    <template v-slot:footer>
      <div class="w-full justify-end">
        <ds-button
          size="small"
          type="button"
          variant="rounded-secondary"
          @click="closeModal"
          :disabled="loading"
          :label="$t('cancel')"
        >
        </ds-button>
        <ds-button
          size="small"
          type="button"
          variant="rounded"
          @click="submitTestimonial"
          :disabled="!canSubmit || loading"
          :loading="loading"
          :label="$t('create_testimonial_submit')"
        >
        </ds-button>
      </div>
    </template>
  </modal>
</template>

<script>
  import * as TestimonialsAPI from '../../Domain/Actor/Api/testimonials'
  import Modal from '@/components/Modals/Modal.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import DsTextarea from '@/components/Form/DsTextarea.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import DsInput from '@/components/Form/DsInput.vue'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import UserMixin from '@/util/UserMixin'
  import { actorNameAutoComplete } from '@/api/actors'
  import debounce from 'lodash/debounce.js'

  export default {
    name: 'CreateTestimonialModal',
    mixins: [TranslationsMixin, UserMixin],
    components: { Dropdown, DsInput, DsTextarea, Modal },
    data () {
      return {
        modalId: MODAL_IDS.CREATE_TESTIMONIAL,
        eligibleActors: [],
        relationshipTypes: [],
        loading: false,
        loadingEligible: false,
        selectedTargetActor: null,
        actorSearchQuery: '',
        actorSearchResults: [],
        actorSearchLoading: false,
        actorSearchOpen: false,
        actorSearchDropdownStyle: {},
        form: {
          author_actor_id: '',
          content: '',
          rating: null,
          author_role: '',
          relationship_type: ''
        },
        errors: {}
      }
    },
    computed: {
      modalContext () {
        return this.$store.state.ui.modalContext
      },
      targetActor () {
        console.log(this.modalContext.targetActor)
        return this.modalContext.targetActor
      },
      resolvedTargetActor () {
        return this.targetActor || (this.selectedTargetActor ? { id: this.selectedTargetActor.value, name: this.selectedTargetActor.label } : null)
      },
      selectedAuthor () {
        return this.eligibleActors.find(actor => actor.value === this.form.author_actor_id)
      },
      canSubmit () {
        return this.resolvedTargetActor &&
          this.form.author_actor_id &&
          this.form.content &&
          this.form.content.length >= 10 &&
          this.form.content.length <= 2000 &&
          this.isContentValid &&
          this.isRatingValid
      },
      isContentValid () {
        if (!this.form.content) return false
        if (this.form.content.length < 10) return false
        if (this.form.content.length > 2000) return false
        if (this.form.content.trim().length < 10) return false

        // Check for basic profanity/inappropriate content patterns
        const inappropriatePatterns = [
          /\b(spam|fake|scam|fraud)\b/i,
          /\b(hate|violence|abuse)\b/i,
          /^(.)\1{9,}$/, // Repeated characters
          /^[^a-zA-Z]*$/ // Only special characters
        ]

        return !inappropriatePatterns.some(pattern => pattern.test(this.form.content))
      },
      isRatingValid () {
        return this.form.rating === null || (this.form.rating >= 1 && this.form.rating <= 5)
      },
      contentValidationMessage () {
        if (!this.form.content) return ''
        if (this.form.content.length < 10) return this.$t('testimonial_content_too_short')
        if (this.form.content.length > 2000) return this.$t('testimonial_content_too_long')
        if (this.form.content.trim().length < 10) return this.$t('testimonial_content_mostly_whitespace')
        if (!this.isContentValid) return this.$t('testimonial_content_inappropriate')
        return ''
      }
    },
    mounted () {
      this.loadEligibleActors()
      this.loadRelationshipTypes()
      this.resetForm()
    },
    methods: {
      closeModal () {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.CREATE_TESTIMONIAL)
      },

      async loadEligibleActors () {
        if (!this.resolvedTargetActor) return
        this.eligibleActors = this.$store.getters.userClaims
          .map(function (actor) {
            return {
              label: actor.name,
              value: actor.id
            }
          })
      },

      onActorSearchFocus () {
        this.actorSearchOpen = true
        this.updateDropdownPosition()
      },

      onActorSearchBlur () {
        // Small delay so mousedown on a result fires before blur hides the list
        setTimeout(() => { this.actorSearchOpen = false }, 150)
      },

      updateDropdownPosition () {
        const el = this.$refs.actorSearchRef
        if (!el) return
        const rect = el.getBoundingClientRect()
        this.actorSearchDropdownStyle = {
          position: 'fixed',
          top: rect.bottom + 4 + 'px',
          left: rect.left + 'px',
          width: rect.width + 'px',
          zIndex: 9999,
        }
      },

      onActorSearchInput: debounce(async function () {
        if (this.selectedTargetActor) return
        this.updateDropdownPosition()
        if (!this.actorSearchQuery || this.actorSearchQuery.length < 2) {
          this.actorSearchResults = []
          return
        }
        this.actorSearchLoading = true
        try {
          const response = await actorNameAutoComplete(this.actorSearchQuery)
          this.actorSearchResults = Object.values(response).map(actor => ({
            label: actor.name,
            value: actor.id
          }))
        } catch {
          this.actorSearchResults = []
        } finally {
          this.actorSearchLoading = false
        }
      }, 250),

      selectTargetActor (result) {
        this.selectedTargetActor = result
        this.actorSearchQuery = result.label
        this.actorSearchResults = []
        this.actorSearchOpen = false
        this.loadEligibleActors()
      },

      clearTargetActor () {
        this.selectedTargetActor = null
        this.actorSearchQuery = ''
        this.actorSearchResults = []
        this.eligibleActors = []
        this.actorSearchOpen = false
      },

      async loadRelationshipTypes () {
        try {
          const response = await TestimonialsAPI.getRelationshipTypes()
          this.relationshipTypes = Object.entries(response.relationship_types).map(([value, label]) => ({
            label,
            value
          })) || [];
          console.log(this.relationshipTypes)
        } catch (error) {
          console.error('Failed to load relationship types:', error)
        }
      },

      onAuthorChange () {
        const selectedAuthor = this.selectedAuthor
        if (selectedAuthor?.relationship) {
          // Pre-fill relationship type from existing relationship
          this.form.relationship_type = selectedAuthor.relationship.type
          this.form.author_role = this.profile.departmentLabel.charAt(0).toUpperCase() + this.profile.departmentLabel.slice(1) || ''
        } else {
          // Clear if no relationship
          this.form.relationship_type = ''
          this.form.author_role = this.profile.departmentLabel.charAt(0).toUpperCase() + this.profile.departmentLabel.slice(1) || ''
        }
      },

      setRating (rating) {
        this.form.rating = rating
      },

      resetForm () {
        this.form = {
          author_actor_id: '',
          content: '',
          rating: null,
          author_role: '',
          relationship_type: ''
        }
        this.errors = {}
      },

      validateForm () {
        const errors = {}

        if (!this.form.author_actor_id) {
          errors.author_actor_id = [this.$t('testimonial_author_required')]
        }

        if (!this.form.content || this.form.content.trim().length === 0) {
          errors.content = [this.$t('testimonial_content_required')]
        } else if (this.form.content.length < 10) {
          errors.content = [this.$t('testimonial_content_too_short')]
        } else if (this.form.content.length > 2000) {
          errors.content = [this.$t('testimonial_content_too_long')]
        } else if (this.form.content.trim().length < 10) {
          errors.content = [this.$t('testimonial_content_mostly_whitespace')]
        } else if (!this.isContentValid) {
          errors.content = [this.$t('testimonial_content_inappropriate')]
        }

        if (this.form.rating !== null && (this.form.rating < 1 || this.form.rating > 5)) {
          errors.rating = [this.$t('testimonial_rating_invalid')]
        }

        return errors
      },

      async submitTestimonial () {
        // Frontend validation
        const validationErrors = this.validateForm()
        if (Object.keys(validationErrors).length > 0) {
          this.errors = validationErrors
          this.$store.commit('UI/SHOW_TOAST', {
            type: 'error',
            message: this.$t('testimonial_validation_failed')
          })
          return
        }

        if (!this.canSubmit) return

        this.loading = true
        this.errors = {}

        try {
          // Sanitize content
          const sanitizedForm = {
            ...this.form,
            content: this.form.content.trim()
              .replace(/\s+/g, ' ')
              .replace(/\n{3,}/g, '\n\n')
          }

          const response = await TestimonialsAPI.createTestimonial(this.resolvedTargetActor.id, sanitizedForm)

          // Close modal (parent will reload via watcher)
          this.closeModal()

          // Show success message
          this.$store.commit('UI/SHOW_TOAST', {
            type: 'success',
            message: response.message || this.$t('create_testimonial_success')
          })
        } catch (error) {
          if (error.response && error.response.status === 422) {
            this.errors = error.response.data.errors || {}
            if (error.response.data.message) {
              this.$store.commit('UI/SHOW_TOAST', {
                type: 'error',
                message: error.response.data.message
              })
            }
          } else {
            this.$store.commit('UI/SHOW_TOAST', {
              type: 'error',
              message: this.$t('create_testimonial_error')
            })
          }
        } finally {
          this.loading = false
        }
      }
    }
  }
</script>

<style lang="scss">
  .actor-search__dropdown {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;

    &-item {
      padding: 8px 12px;
      font-size: 13px;
      cursor: pointer;
      color: #111827;

      &:hover { background: #f9fafb; }
    }

    &-loading {
      padding: 10px 12px;
      font-size: 13px;
      color: #9ca3af;
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
</style>

<style lang="scss" scoped>
  .actor-search {
    &__selected {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 6px;
      padding: 6px 10px;
      background: var(--primary-lightest, #f0f4ff);
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;

      &--readonly {
        background: #f3f4f6;
        color: #374151;
      }

      button {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 16px;
        color: #6b7280;
        padding: 0 2px;
        line-height: 1;

        &:hover { color: #111; }
      }
    }
  }

  .create-testimonial-modal {
    .collaboration-message {
      margin-bottom: 24px;
      padding: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 8px;
      color: white;

      .message-title {
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: 700;
        color: white;
      }

      .message-text {
        margin: 0;
        font-size: 14px;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.95);
      }
    }

    .relationship-detected-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      margin-bottom: 20px;
      background: #ecfdf5;
      border: 1px solid #a7f3d0;
      border-radius: 6px;
      color: #059669;
      font-size: 14px;

      strong {
        font-weight: 600;
      }
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 40px;
      color: #6b7280;

      span {
        font-size: 14px;
      }
    }

    .no-eligible-actors {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background-color: #fef3cd;
      border: 1px solid #fbeaa7;
      border-radius: 8px;
      margin-bottom: 16px;

      h4 {
        margin: 0 0 8px 0;
        font-weight: 600;
        color: #92400e;
      }

      p {
        margin: 0;
        color: #92400e;
        font-size: 14px;
      }
    }

    .rating-input {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 8px;

      .star-button {
        background: none;
        border: none;
        padding: 4px;
        cursor: pointer;
        color: #d1d5db;
        transition: color 0.2s;

        &:hover {
          color: #fbbf24;
        }

        &.active {
          color: #f59e0b;
        }
      }

      .clear-rating {
        background: none;
        border: none;
        color: #6b7280;
        font-size: 12px;
        cursor: pointer;
        margin-left: 8px;
        text-decoration: underline;

        &:hover {
          color: #374151;
        }
      }
    }


    .form-help-text {
      font-size: 12px;
      color: #6b7280;
      margin-top: 4px;
      margin-bottom: 0;

      &.text-red-500 {
        color: #ef4444;
      }
    }

    .textarea-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      gap: 8px;

      .character-count {
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
        padding: 4px 8px;
        background: #f8fafc;
        border-radius: 6px;
        flex-shrink: 0;

        &.text-red-500 {
          color: #ef4444;
          background: #fef2f2;
        }

        &.text-green-500 {
          color: #10b981;
          background: #f0fdf4;
        }
      }
    }

    .invalid {
      border-color: #ef4444 !important;
      background-color: #fef2f2 !important;
    }
  }
</style>
